import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Form, Checkbox, Table, Button, Dropdown } from 'semantic-ui-react';
import { Input } from '../../../lib/custom-ui';

import styles from '../SettingDashboard.module.scss';

const ComponentType7Form = React.memo(
  ({
    devices,
    items,
    data,
    isSubmitting,
    onChangeField,
    onChangeEmptyField,
    onChangeDeviceIdsGroupField,
    isError,
  }) => {
    const [t] = useTranslation();

    const devicesOptions = [];
    devices
      .filter((item) => item.logicDevice !== true)
      .forEach((item) => {
        devicesOptions.push({ key: item.id, value: item.id, text: item.name });
      });

    const itemsOptions = [];
    items.forEach((item) => {
      itemsOptions.push({ key: item.id, value: item.itemName, text: item.name });
    });

    const colorsOptions = [
      { key: 0, value: '#FFFFFF', text: 'White' },
      { key: 1, value: '#C0C0C0', text: 'Silver' },
      { key: 2, value: '#808080', text: 'Gray' },
      { key: 3, value: '#000000', text: 'Black' },
      { key: 4, value: '#FF0000', text: 'Red' },
      { key: 5, value: '#800000', text: 'Maroon' },
      { key: 6, value: '#FFFF00', text: 'Yellow' },
      { key: 7, value: '#808000', text: 'Olive' },
      { key: 8, value: '#00FF00', text: 'Lime' },
      { key: 9, value: '#008000', text: 'Green' },
      { key: 10, value: '#00FFFF', text: 'Aqua' },
      { key: 11, value: '#008080', text: 'Teal' },
      { key: 12, value: '#0000FF', text: 'Blue' },
      { key: 13, value: '#000080', text: 'Navy' },
      { key: 14, value: '#FF00FF', text: 'Fuchsia' },
      { key: 15, value: '#800080', text: 'Purple' },
    ];

    const descriptionField = useRef(null);
    const titleField = useRef(null);
    const intervalField = useRef(null);
    const unitNameField = useRef(null);
    const decimalPointField = useRef(null);
    const nameField = useRef(null);

    const positionOptions = [
      { key: 16, value: 16, text: 16 },
      { key: 17, value: 17, text: 17 },
    ];

    const [fields, setFields] = useState(data.deviceIdsGroup);

    const handleChange = (i, event) => {
      const values = [...fields];
      values[i][event.target.name] = event.target.value;
      setFields(values);
    };

    const handleChangeSelect = (i, event, dataValue) => {
      const values = [...fields];
      values[i][dataValue.name] = dataValue.value;
      setFields(values);
    };

    const handleAdd = () => {
      const values = [...fields];
      values.push({ name: '', color: '', deviceIds: [] });
      setFields(values);
    };

    const handleRemove = (i) => {
      const values = [...fields];
      values.splice(i, 1);
      setFields(values);
    };

    useEffect(() => {
      onChangeDeviceIdsGroupField(fields);
    }, [fields, onChangeDeviceIdsGroupField]);

    const [positionError, setPositionError] = useState(false);
    const [itemNameError, setItemNameError] = useState(false);
    const [descriptionError, setDescriptionError] = useState(false);
    const [unitNameError, setUnitNameError] = useState(false);
    const [decimalPointError, setDecimalPointError] = useState(false);
    const [titleError, setTitleError] = useState(false);
    const [intervalError, setIntervalError] = useState(false);
    useEffect(() => {
      setPositionError(isError.indexOf('position') !== -1);
      setUnitNameError(isError.indexOf('unitName') !== -1);
      setDecimalPointError(isError.indexOf('decimalPoint') !== -1);
      setDescriptionError(isError.indexOf('description') !== -1);
      setTitleError(isError.indexOf('title') !== -1);
      setIntervalError(isError.indexOf('interval') !== -1);
      setItemNameError(isError.indexOf('itemName') !== -1);
    }, [isError]);

    return (
      <>
        <Form.Field required error={positionError} onFocus={() => setPositionError(false)}>
          <label htmlFor="position">{t('common.components.position')}</label>
          <Form.Select
            name="position"
            className={styles.field}
            placeholder={t('common.selectPosition')}
            search
            fluid
            selection
            value={data.position}
            options={positionOptions}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={itemNameError} onFocus={() => setItemNameError(false)}>
          <label htmlFor="itemName">{t('common.components.items')}</label>
          <Form.Select
            name="itemName"
            className={styles.field}
            placeholder={t('common.selectItem')}
            search
            fluid
            selection
            value={data.itemName}
            options={itemsOptions}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={descriptionError} onFocus={() => setDescriptionError(false)}>
          <label htmlFor="description">{t('common.components.description')}</label>
          <Input
            fluid
            ref={descriptionField}
            name="description"
            value={data.description}
            readOnly={isSubmitting}
            className={styles.field}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field>
          <Checkbox
            toggle
            label={t('common.components.empty')}
            name="empty"
            defaultChecked={data.empty === true}
            readOnly={isSubmitting}
            onChange={onChangeEmptyField}
          />
        </Form.Field>
        <Form.Field required error={unitNameError} onFocus={() => setUnitNameError(false)}>
          <label htmlFor="unitName">{t('common.components.unitName')}</label>
          <Input
            fluid
            ref={unitNameField}
            name="unitName"
            value={data.unitName}
            readOnly={isSubmitting}
            className={styles.field}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={titleError} onFocus={() => setTitleError(false)}>
          <label htmlFor="title">{t('common.components.title')}</label>
          <Input
            fluid
            ref={titleField}
            name="title"
            value={data.title}
            readOnly={isSubmitting}
            className={styles.field}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={decimalPointError} onFocus={() => setDecimalPointError(false)}>
          <label htmlFor="decimalPoint">{t('common.components.decimalPoint')}</label>
          <Input
            fluid
            type="number"
            ref={decimalPointField}
            name="decimalPoint"
            value={data.decimalPoint}
            readOnly={isSubmitting}
            className={styles.field}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={intervalError} onFocus={() => setIntervalError(false)}>
          <label htmlFor="interval">{t('common.components.interval')}</label>
          <Input
            fluid
            type="number"
            ref={intervalField}
            name="interval"
            value={data.interval}
            readOnly={isSubmitting}
            className={styles.field}
            onChange={onChangeField}
          />
        </Form.Field>

        <Table className={styles.items_warpper}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('common.components.ordinalNumbers')}</Table.HeaderCell>
              <Table.HeaderCell>{t('common.components.name')}</Table.HeaderCell>
              <Table.HeaderCell>{t('common.components.color')}</Table.HeaderCell>
              <Table.HeaderCell>{t('common.components.devices')}</Table.HeaderCell>
              <Table.HeaderCell>&nbsp;</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {fields.map((field, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Table.Row key={index}>
                <Table.Cell singleLine>{index + 1}</Table.Cell>
                <Table.Cell singleLine>
                  <Input
                    fluid
                    name="name"
                    ref={nameField}
                    value={field.name}
                    readOnly={isSubmitting}
                    className={styles.field}
                    onChange={(e) => handleChange(index, e)}
                  />
                </Table.Cell>
                <Table.Cell singleLine>
                  <Dropdown
                    name="color"
                    className={styles.field}
                    placeholder={t('common.selectColor')}
                    search
                    fluid
                    selection
                    value={field.color}
                    options={colorsOptions}
                    onChange={(e, { name, value }) => handleChangeSelect(index, e, { name, value })}
                  />
                </Table.Cell>
                <Table.Cell singleLine>
                  <Dropdown
                    multiple
                    name="deviceIds"
                    className={styles.field}
                    placeholder={t('common.selectDevice')}
                    search
                    fluid
                    selection
                    value={field.deviceIds}
                    options={devicesOptions}
                    onChange={(e, { name, value }) => handleChangeSelect(index, e, { name, value })}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Button
                    className={styles.buttonRemoveItem}
                    type="button"
                    onClick={() => handleRemove(index)}
                  >
                    {t('common.components.removeDevice')}
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Footer fullWidth>
            <Table.Row>
              <Table.HeaderCell colSpan="5">
                <Button className={styles.buttonAddLevel} type="button" onClick={() => handleAdd()}>
                  {t('common.components.addDevices')}
                </Button>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </>
    );
  },
);

ComponentType7Form.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.any).isRequired,
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onChangeField: PropTypes.func.isRequired,
  onChangeEmptyField: PropTypes.func.isRequired,
  onChangeDeviceIdsGroupField: PropTypes.func.isRequired,
  isError: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default ComponentType7Form;
